<Head :title="user.name ? user.name : user.username" />
<template>
    <div
        class="page-wrapper"
        :class="{
            'hero-page': appearance?.profile_layout === 'hero',
            'has-impersonation-bar':
                impersonationWithTargetLabel?.isImpersonating ||
                impersonationWithTargetLabel?.canImpersonateTarget,
        }"
        :style="{
            '--userprofile-social-icons': socialIconsColor,
            '--userprofile-font-color': appearance.fonts_color,
        }"
    >
        <div class="overlay"></div>
        <impersonation-bar
            :impersonation="impersonationWithTargetLabel"
            :showStartButton="true"
        />
        <!-- your card -->
        <div
            class="profile-card flex flex-col"
            :class="{
                'full-height': !hasAnyLinks,
                'hero-mode': appearance?.profile_layout === 'hero',
            }"
            :style="profileCardStyle"
        >
            <div v-if="appearance?.animated_glow">
                <div class="animated-glow--mobile d-lg-none">
                    <AnimatedGlow v-if="appearance?.animated_glow" />
                </div>
                <div class="animated-glow--desktop d-none d-lg-block">
                    <AnimatedGlow
                        :inline="true"
                        v-if="appearance?.animated_glow"
                    />
                </div>
            </div>

            <div class="profile-header">
                <!-- MRegisterPopup -->
                <button
                    class="btn-settings"
                    aria-label="Settings"
                    @click="openMRegisterPopup"
                    :style="headerIconStyle"
                    :class="{ 'icon-has-bg': headerIconHasBackground }"
                >
                    <LogoAllMyOnline :size="18" />
                </button>
                <!-- SharePopup -->
                <button
                    class="btn-share"
                    aria-label="Share"
                    @click="openSharePopup()"
                    :style="headerIconStyle"
                    :class="{ 'icon-has-bg': headerIconHasBackground }"
                >
                    <IconShare :size="18" />
                </button>
            </div>

            <div
                v-if="showEmailVerificationBanner"
                class="profile-email-verification-banner"
                role="alert"
                aria-live="polite"
            >
                <div class="profile-email-verification-banner__text font-title">
                    <strong>Your profile is not live yet.</strong>
                    People cannot see your profile until you verify your email.
                    <div
                        v-if="emailVerificationEmailSent"
                        class="profile-email-verification-banner__sent"
                    >
                        Verification email sent. Check your inbox and spam
                        folder.
                    </div>
                </div>
                <button
                    type="button"
                    class="amo-btn amo-btn--danger amo-btn--sm profile-email-verification-banner__action"
                    :class="{
                        'profile-email-verification-banner__action--sent':
                            emailVerificationEmailSent,
                    }"
                    :disabled="
                        isResendingEmailVerification ||
                        emailVerificationEmailSent
                    "
                    @click="resendEmailVerification"
                >
                    <span
                        v-if="isResendingEmailVerification"
                        class="spinner-border spinner-border-sm"
                        role="status"
                    ></span>
                    <span v-else-if="emailVerificationEmailSent">Sent</span>
                    <span v-else>Resend</span>
                </button>
            </div>

            <MRegisterPopup
                :show="showMRegister"
                :urlName="urlName"
                :referral_code="user?.referral_code || null"
                @close="closeMRegisterPopup"
            />

            <ProfileIdentity :user="user" :appearance="appearance" />
            <div class="icons-list icons-list--top" v-if="isTopWithIcons()">
                <!-- Swiper Container for more than 6 icons -->
                <div class="mt-3" v-if="visibleSocialIcons.length > 5">
                    <Carousel
                        :itemsToShow="6"
                        :wrapAround="true"
                        :autoplay="2500"
                        :pauseAutoplayOnHover="true"
                        :transition="500"
                        class="mySwiper"
                    >
                        <Slide
                            v-for="(icon, index) in visibleSocialIcons"
                            :key="index"
                        >
                            <a
                                target="_blank"
                                class="social-card-link"
                                :class="'icon-style-' + appearance.icon_style"
                                :href="socialIconHref(icon)"
                                :style="getStyleForIcon(appearance)"
                            >
                                <span
                                    class="social-icon-svg social-icon-svg--styled"
                                    v-if="iconColorType === 'mono'"
                                    v-html="
                                        getStyledSvg(
                                            icon.svg_icon.platform_slug
                                        )
                                    "
                                ></span>
                                <i
                                    v-else-if="icon.svg_icon"
                                    class="social-icon-svg"
                                    v-html="
                                        getUnstyledSvg(
                                            icon.svg_icon.platform_slug
                                        )
                                    "
                                ></i>
                            </a>
                        </Slide>
                    </Carousel>
                </div>

                <!-- Static Layout for 6 or fewer icons -->
                <div
                    v-else
                    class="btn-container d-flex flex-wrap justify-content-center gap-2"
                >
                    <div
                        v-for="icon in visibleSocialIcons"
                        :key="icon.id"
                        class="btn-wrapper"
                    >
                        <a
                            target="_blank"
                            class="social-card-link"
                            :class="'icon-style-' + appearance.icon_style"
                            :href="socialIconHref(icon)"
                            :style="getStyleForIcon(appearance)"
                            @click="saveClickedSocialIcon(icon.svg_icon)"
                        >
                            <span
                                class="social-icon-svg social-icon-svg--styled"
                                v-if="iconColorType === 'mono'"
                                v-html="
                                    getStyledSvg(icon.svg_icon.platform_slug)
                                "
                            ></span>
                            <i
                                class="social-icon-svg"
                                v-else-if="icon.svg_icon"
                                v-html="
                                    getUnstyledSvg(icon.svg_icon.platform_slug)
                                "
                            ></i>
                        </a>
                    </div>
                </div>
            </div>

            <FollowerStatsDisplay
                v-if="shouldShowFollowerStats"
                :total-followers="followerStatsSettings.total_followers"
                :accounts="followerStatsSettings.accounts"
                :appearance="appearance"
                :url-name="urlName"
            />

            <!-- Collect Emails Form -->
            <CollectEmailsForm
                v-if="collectEmailsSettings?.active"
                :user="user"
                :appearance="appearance"
                :page-id="currentPageId"
                :collect-emails-settings="collectEmailsSettings"
                :preview-mode="previewMode"
            />

            <!-- link pills -->

            <!-- Pinned Shop Block: displays collections and products if available -->
            <div class="main" style="padding: 10px">
                <div
                    v-if="
                        user &&
                        user.store_visible &&
                        ((getCollectionsWithProducts &&
                            getCollectionsWithProducts.length) ||
                            (products && products.length))
                    "
                    class="links flex-1"
                >
                    <ShopCard
                        :products="products"
                        :getCollectionsWithProducts="getCollectionsWithProducts"
                        :urlName="urlName"
                        :appearance="appearance"
                        :DoEndpoint="DoEndpoint"
                        :DoBucket="DoBucket"
                        :user="user"
                        :buttonStyle="buttonStyle"
                    />
                </div>
                <!-- General Links -->
                <div class="links flex-1" v-for="link in links" :key="link.id">
                    <!-- Free plan: render GeneralButton for all except contact-details, contact-form, and header -->
                    <GeneralButton
                        v-if="
                            user &&
                            user.plan === 'free' &&
                            link.platform !== 'contact-details' &&
                            link.platform !== 'contact-form' &&
                            link.platform !== 'header' &&
                            link.platform !== 'shopify'
                        "
                        :link="link"
                        :appearance="appearance"
                        :button-style="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        :layout="link.options?.layout || 'classic'"
                        :prevent-nav="true"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    >
                    </GeneralButton>
                    <!-- featured BUTTON and platform youtube -->
                    <YouTubeCard
                        v-else-if="
                            link.platform === 'youtube' &&
                            ['embed', 'latest', 'link', 'latest_link'].includes(
                                link.options?.youtube?.display_mode
                            )
                        "
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                $event,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <DeezerCard
                        v-else-if="link.platform === 'deezer'"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <SoundCloudCard
                        v-else-if="link.platform === 'soundcloud'"
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <TwitchCard
                        v-else-if="link.platform === 'twitch'"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <ShopifyCard
                        v-else-if="link.platform === 'shopify'"
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <TypeformCard
                        v-else-if="link.platform === 'typeform'"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <TikTokCard
                        v-else-if="link.platform === 'tiktok'"
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <XCard
                        v-else-if="link.platform === 'x'"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <CalendlyCard
                        v-else-if="link.platform === 'calendly'"
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <FacebookCard
                        v-else-if="link.platform === 'facebook'"
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <GoogleMapCard
                        v-else-if="link.platform === 'google-maps'"
                        :ref="`card_${link.id}`"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <ContactDetailsCard
                        v-else-if="link.platform === 'contact-details'"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />
                    <ContactFormCard
                        v-else-if="link.platform === 'contact-form'"
                        :link="link"
                        :appearance="appearance"
                        :buttonStyle="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    />

                    <div
                        :id="link.id"
                        class="w-100 d-flex justify-content-center align-items-center mt-3 mb-4"
                        v-else-if="link.platform === 'header'"
                    >
                        <!-- Centered Text for Header Link Type -->
                        <p
                            class="text"
                            style=""
                            v-bind:style="[
                                {
                                    // Headers should follow the configured font color (not button text color).
                                    color:
                                        appearance.fonts_color ||
                                        appearance.button_font_color,
                                    'font-family': appearance.fonts,
                                    'font-weight':
                                        cssFontWeight,
                                },
                            ]"
                        >
                            {{ link.title.substring(0, 30) }}
                        </p>
                    </div>
                    <GeneralButton
                        v-else
                        :link="link"
                        :appearance="appearance"
                        :button-style="buttonStyle"
                        :DoBucket="DoBucket"
                        :DoEndpoint="DoEndpoint"
                        :layout="link.options?.layout || 'classic'"
                        :prevent-nav="true"
                        @click="
                            handleClick(
                                link.url,
                                link.user_id,
                                link.platform,
                                link
                            )
                        "
                        @share="openSharePopup(link)"
                    >
                    </GeneralButton>

                    <!-- end important link -->
                </div>
            </div>
            <!-- Footer Section -->
            <div
                class="footer-section mt-auto pt-4"
                v-show="activeTab === 'links'"
            >
                <div
                    class="mb-3 icons-list icons-list--bottom"
                    v-if="isBottomWithIcons()"
                >
                    <div class="mt-3" v-if="visibleSocialIcons.length > 5">
                        <Carousel
                            :itemsToShow="5"
                            :wrapAround="true"
                            :autoplay="2500"
                            :pauseAutoplayOnHover="true"
                            :transition="500"
                        >
                            <Slide
                                v-for="(icon, index) in visibleSocialIcons"
                                :key="index"
                            >
                                <a
                                    target="_blank"
                                    class="social-card-link"
                                    :class="
                                        'icon-style-' + appearance.icon_style
                                    "
                                    :href="socialIconHref(icon)"
                                    :style="getStyleForIcon(appearance)"
                                >
                                    <span
                                        class="social-icon-svg social-icon-svg--styled"
                                        v-if="iconColorType === 'mono'"
                                        v-html="
                                            getStyledSvg(
                                                icon.svg_icon.platform_slug
                                            )
                                        "
                                    ></span>
                                    <i
                                        class="social-icon-svg"
                                        v-else-if="icon.svg_icon"
                                        v-html="
                                            getUnstyledSvg(
                                                icon.svg_icon.platform_slug
                                            )
                                        "
                                    ></i>
                                </a>
                            </Slide>
                        </Carousel>
                    </div>
                    <div
                        v-else
                        class="btn-container d-flex flex-wrap justify-content-center gap-2 mt-3"
                    >
                        <div
                            v-for="icon in visibleSocialIcons"
                            :key="icon.id"
                            class="btn-wrapper"
                        >
                            <a
                                target="_blank"
                                class="social-card-link"
                                :class="'icon-style-' + appearance.icon_style"
                                :href="socialIconHref(icon)"
                                :style="getStyleForIcon(appearance)"
                            >
                                <span
                                    class="social-icon-svg social-icon-svg--styled"
                                    v-if="iconColorType === 'mono'"
                                    v-html="
                                        getStyledSvg(
                                            icon.svg_icon.platform_slug
                                        )
                                    "
                                ></span>
                                <i
                                    class="social-icon-svg"
                                    v-else-if="icon.svg_icon"
                                    v-html="
                                        getUnstyledSvg(
                                            icon.svg_icon.platform_slug
                                        )
                                    "
                                ></i>
                            </a>
                        </div>
                    </div>
                </div>

                <button
                    class="app-button--secondary mt-10 mb-5"
                    @click="goSignup"
                >
                    <svg
                        viewBox="0 0 4288 4096"
                        id="svgContainer"
                        xmlns="http://www.w3.org/2000/svg"
                        width="1rem"
                    >
                        <path
                            d=""
                            stroke="none"
                            fill="currentColor"
                            fill-rule="evenodd"
                        ></path>
                        <path
                            d="M 2133.744 1.114 C 2126.222 1.840, 2118.227 2.955, 2102 5.542 C 2064.975 11.445, 1998.244 37.123, 1966.771 57.577 C 1964.721 58.910, 1962.772 60, 1962.441 60 C 1962.110 60, 1959.288 61.643, 1956.169 63.652 C 1953.051 65.660, 1948.925 68.281, 1947 69.475 C 1883.263 109.016, 1818.769 168.838, 1756.479 246.195 C 1755.728 247.127, 1750.926 253.427, 1745.807 260.195 C 1737.103 271.702, 1732.742 277.717, 1723.604 290.814 C 1720.618 295.093, 1416.259 801.661, 1409.260 814 C 1408.168 815.925, 1382.989 858, 1353.305 907.500 C 1268.128 1049.543, 1229.402 1114.219, 1227.266 1118 C 1226.179 1119.925, 1211.126 1145.125, 1193.816 1174 C 1164.594 1222.745, 1132.529 1276.344, 1084.039 1357.500 C 1073.195 1375.650, 1055.290 1405.602, 1044.251 1424.059 C 1033.212 1442.517, 1023.303 1459.167, 1022.231 1461.059 C 1021.158 1462.952, 1011.386 1479.350, 1000.514 1497.500 C 989.641 1515.650, 971.073 1546.700, 959.250 1566.500 C 947.428 1586.300, 932.238 1611.725, 925.495 1623 C 918.752 1634.275, 912.348 1645.075, 911.264 1647 C 910.180 1648.925, 903.099 1660.850, 895.527 1673.500 C 887.955 1686.150, 874.781 1708.200, 866.252 1722.500 C 857.723 1736.800, 845.094 1757.947, 838.187 1769.493 C 831.280 1781.039, 825.147 1791.389, 824.557 1792.493 C 823.021 1795.370, 776.495 1873.284, 771.869 1880.725 C 767.607 1887.584, 767.113 1889.414, 769.750 1888.584 C 792.770 1881.343, 869.603 1867.577, 923.500 1861.038 C 932.850 1859.903, 942.075 1858.776, 944 1858.532 C 951.608 1857.568, 975.450 1855.173, 984 1854.513 C 988.125 1854.195, 993.300 1853.724, 995.500 1853.466 C 1001.172 1852.800, 1037.641 1850.280, 1063 1848.801 C 1092.728 1847.068, 1186.565 1847.083, 1203 1848.824 C 1223.657 1851.012, 1244.652 1853.884, 1260 1856.623 C 1289.186 1861.831, 1291.247 1862.262, 1323 1869.780 C 1340.620 1873.952, 1372.750 1882.516, 1405 1891.637 C 1413.525 1894.048, 1422.300 1896.527, 1424.500 1897.145 C 1431.819 1899.204, 1434.405 1899.947, 1448 1903.894 C 1455.425 1906.050, 1466 1909.041, 1471.500 1910.541 C 1477 1912.040, 1484.141 1914.101, 1487.370 1915.119 C 1494.405 1917.338, 1493.671 1917.716, 1498.639 1909.310 C 1500.853 1905.565, 1508.644 1892.375, 1515.952 1880 C 1550.362 1821.728, 1555.713 1812.695, 1562.614 1801.225 C 1566.676 1794.473, 1570 1788.744, 1570 1788.494 C 1570 1788.244, 1571.463 1785.657, 1573.250 1782.745 C 1575.037 1779.833, 1577.041 1776.562, 1577.702 1775.476 C 1578.928 1773.460, 1596.365 1743.948, 1607.526 1725 C 1610.928 1719.225, 1616.701 1709.438, 1620.355 1703.250 C 1624.010 1697.063, 1629.853 1687.163, 1633.340 1681.250 C 1636.827 1675.338, 1646.358 1659.250, 1654.520 1645.500 C 1662.682 1631.750, 1669.847 1619.600, 1670.443 1618.500 C 1671.040 1617.400, 1677.537 1606.375, 1684.882 1594 C 1692.227 1581.625, 1701.400 1566.100, 1705.266 1559.500 C 1709.133 1552.900, 1713.523 1545.475, 1715.021 1543 C 1716.519 1540.525, 1722.360 1530.625, 1728 1521 C 1733.640 1511.375, 1739.488 1501.475, 1740.996 1499 C 1744.410 1493.394, 1769.044 1451.756, 1771.759 1447 C 1774.002 1443.072, 1791.519 1413.328, 1792.961 1411 C 1793.472 1410.175, 1797.598 1403.200, 1802.131 1395.500 C 1806.664 1387.800, 1812.245 1378.350, 1814.534 1374.500 C 1816.822 1370.650, 1823.321 1359.625, 1828.975 1350 C 1834.629 1340.375, 1840.488 1330.475, 1841.996 1328 C 1845.410 1322.394, 1870.044 1280.756, 1872.759 1276 C 1875.002 1272.072, 1892.519 1242.328, 1893.961 1240 C 1894.472 1239.175, 1898.598 1232.200, 1903.130 1224.500 C 1907.662 1216.800, 1913.919 1206.225, 1917.033 1201 C 1920.148 1195.775, 1926.647 1184.750, 1931.476 1176.500 C 1936.304 1168.250, 1941.488 1159.475, 1942.996 1157 C 1944.503 1154.525, 1951.694 1142.375, 1958.975 1130 C 1966.256 1117.625, 1975.540 1101.976, 1979.606 1095.225 C 1983.673 1088.473, 1987 1082.744, 1987 1082.494 C 1987 1082.244, 1988.463 1079.657, 1990.250 1076.745 C 1992.037 1073.833, 1994.040 1070.562, 1994.700 1069.476 C 1997.186 1065.381, 2008.568 1046.193, 2013.074 1038.500 C 2018.913 1028.531, 2028.663 1011.967, 2037.355 997.250 C 2041.010 991.063, 2046.853 981.163, 2050.340 975.250 C 2053.827 969.337, 2063.358 953.250, 2071.520 939.500 C 2079.682 925.750, 2086.850 913.600, 2087.451 912.500 C 2088.051 911.400, 2094.550 900.375, 2101.893 888 C 2109.236 875.625, 2118.406 860.100, 2122.270 853.500 C 2126.135 846.900, 2130.523 839.475, 2132.021 837 C 2133.519 834.525, 2139.365 824.625, 2145.011 815 C 2150.657 805.375, 2156.718 795.153, 2158.480 792.285 L 2161.683 787.069 2166.092 794.610 C 2173.060 806.527, 2173.349 807.015, 2190.991 836.500 C 2217.709 881.155, 2248.629 933.179, 2281.033 988 C 2289.973 1003.125, 2299.164 1018.650, 2301.458 1022.500 C 2303.751 1026.350, 2308.807 1034.900, 2312.694 1041.500 C 2316.581 1048.100, 2324.013 1060.700, 2329.210 1069.500 C 2334.408 1078.300, 2341.947 1091.125, 2345.965 1098 C 2349.983 1104.875, 2354.499 1112.525, 2356 1115 C 2357.501 1117.475, 2362.001 1125.125, 2366 1132 C 2369.999 1138.875, 2374.499 1146.525, 2376 1149 C 2377.501 1151.475, 2382.015 1159.125, 2386.031 1166 C 2398.325 1187.045, 2403.179 1195.306, 2406.013 1200 C 2407.507 1202.475, 2411.885 1209.900, 2415.742 1216.500 C 2419.599 1223.100, 2428.044 1237.500, 2434.508 1248.500 C 2440.973 1259.500, 2449.276 1273.675, 2452.960 1280 C 2456.644 1286.325, 2463.630 1298.250, 2468.484 1306.500 C 2473.338 1314.750, 2481.119 1328.025, 2485.774 1336 C 2494.484 1350.921, 2504.560 1368.041, 2519.027 1392.500 C 2523.581 1400.200, 2528.190 1408.075, 2529.270 1410 C 2531.812 1414.533, 2543.920 1435.245, 2550.024 1445.500 C 2552.642 1449.900, 2558.718 1460.250, 2563.526 1468.500 C 2568.333 1476.750, 2574.148 1486.650, 2576.446 1490.500 C 2578.745 1494.350, 2583.256 1502, 2586.471 1507.500 C 2589.686 1513, 2594.186 1520.650, 2596.471 1524.500 C 2598.756 1528.350, 2603.256 1536, 2606.471 1541.500 C 2609.686 1547, 2614.186 1554.650, 2616.471 1558.500 C 2618.756 1562.350, 2623.256 1570, 2626.471 1575.500 C 2629.686 1581, 2634.186 1588.650, 2636.471 1592.500 C 2638.756 1596.350, 2643.135 1603.775, 2646.202 1609 C 2658.954 1630.727, 2678.706 1664.170, 2684.296 1673.500 C 2687.591 1679, 2691.055 1684.850, 2691.994 1686.500 C 2692.933 1688.150, 2695.971 1693.325, 2698.746 1698 C 2701.521 1702.675, 2710.975 1718.650, 2719.756 1733.500 C 2753.613 1790.757, 2765.798 1811.293, 2785.027 1843.500 C 2792.579 1856.150, 2805.178 1877.300, 2813.024 1890.500 C 2830.400 1919.733, 2828.331 1917.331, 2834.518 1915.450 C 2838.765 1914.158, 2886.555 1900.497, 2895.500 1898.017 C 2897.150 1897.559, 2903.450 1895.753, 2909.500 1894.002 C 2915.550 1892.251, 2923.425 1890.012, 2927 1889.025 C 3016.324 1864.367, 3042.185 1858.819, 3111 1849.548 C 3131.488 1846.787, 3243.158 1846.770, 3271 1849.523 C 3273.475 1849.768, 3283.825 1850.452, 3294 1851.043 C 3304.175 1851.634, 3314.075 1852.298, 3316 1852.519 C 3317.925 1852.740, 3325.350 1853.402, 3332.500 1853.991 C 3339.650 1854.579, 3347.525 1855.268, 3350 1855.522 C 3361.616 1856.711, 3391.822 1860.084, 3394.500 1860.491 C 3396.150 1860.742, 3399.525 1861.178, 3402 1861.462 C 3407.839 1862.130, 3436.755 1865.957, 3440 1866.492 C 3441.375 1866.718, 3447.900 1867.799, 3454.500 1868.893 C 3461.100 1869.986, 3467.850 1871.084, 3469.500 1871.333 C 3474.596 1872.099, 3485.252 1873.944, 3490.500 1874.967 C 3513.336 1879.422, 3529.752 1882.988, 3543.561 1886.490 C 3556.045 1889.657, 3556.244 1891.420, 3541.362 1867.012 C 3534.288 1855.409, 3527.781 1844.697, 3526.903 1843.208 C 3524.855 1839.734, 3472.384 1753.542, 3459.509 1732.500 C 3454.124 1723.700, 3445.351 1709.300, 3440.014 1700.500 C 3434.677 1691.700, 3424.728 1675.383, 3417.905 1664.241 C 3411.082 1653.098, 3405.046 1643.198, 3404.491 1642.241 C 3403.935 1641.283, 3400.785 1636.083, 3397.491 1630.684 C 3394.196 1625.286, 3390.779 1619.661, 3389.899 1618.184 C 3387.039 1613.390, 3343.509 1541.887, 3334.996 1528 C 3330.445 1520.575, 3325.499 1512.475, 3324.006 1510 C 3319.065 1501.812, 3293.502 1459.833, 3280.899 1439.212 C 3274.080 1428.053, 3267.781 1417.703, 3266.902 1416.212 C 3264.762 1412.581, 3208.450 1320.069, 3197.991 1303 C 3193.441 1295.575, 3185.351 1282.300, 3180.014 1273.500 C 3174.677 1264.700, 3164.728 1248.383, 3157.905 1237.241 C 3151.082 1226.098, 3145.046 1216.198, 3144.491 1215.241 C 3143.935 1214.283, 3140.785 1209.083, 3137.491 1203.684 C 3134.196 1198.286, 3130.779 1192.661, 3129.899 1191.184 C 3127.039 1186.390, 3083.509 1114.887, 3074.996 1101 C 3070.445 1093.575, 3065.499 1085.475, 3064.006 1083 C 3058.297 1073.539, 3032.766 1031.630, 3020.899 1012.241 C 3014.080 1001.098, 3008.046 991.198, 3007.491 990.241 C 3006.935 989.283, 3003.785 984.083, 3000.491 978.684 C 2997.196 973.286, 2993.782 967.661, 2992.905 966.184 C 2991.505 963.828, 2966.266 922.360, 2935.567 871.977 C 2930.030 862.890, 2916.500 840.725, 2905.500 822.722 C 2894.500 804.719, 2885.046 789.204, 2884.491 788.244 C 2883.935 787.285, 2880.785 782.083, 2877.491 776.684 C 2874.196 771.286, 2870.779 765.661, 2869.899 764.184 C 2867.039 759.390, 2823.509 687.887, 2814.996 674 C 2810.445 666.575, 2805.499 658.475, 2804.006 656 C 2798.297 646.539, 2772.766 604.630, 2760.899 585.241 C 2754.080 574.098, 2748.046 564.198, 2747.491 563.241 C 2746.935 562.283, 2743.785 557.083, 2740.491 551.684 C 2737.196 546.286, 2733.782 540.661, 2732.905 539.184 C 2732.028 537.708, 2721.034 519.625, 2708.473 499 C 2695.913 478.375, 2683.309 457.675, 2680.465 453 C 2677.620 448.325, 2671.035 437.525, 2665.831 429 C 2631.275 372.387, 2611.283 339.580, 2609.905 337.226 C 2579.327 284.976, 2535.344 223.242, 2501.067 184.464 C 2381.126 48.770, 2261.062 -11.160, 2133.744 1.114 M 996 2008.078 C 955.973 2010.454, 920.191 2014.181, 886.988 2019.433 C 878.470 2020.780, 869.700 2022.147, 867.500 2022.470 C 865.300 2022.794, 860.125 2023.726, 856 2024.542 C 851.875 2025.357, 846.250 2026.467, 843.500 2027.007 C 840.750 2027.548, 834 2028.945, 828.500 2030.112 C 823 2031.279, 816.700 2032.602, 814.500 2033.052 C 771.561 2041.828, 690.585 2067.583, 643.500 2087.438 C 613.649 2100.026, 563.365 2124.073, 544.574 2134.747 C 540.133 2137.269, 534.025 2140.676, 531 2142.319 C 527.975 2143.961, 524.306 2146.137, 522.846 2147.153 C 521.386 2148.169, 519.936 2149, 519.623 2149 C 517.040 2149, 456.416 2188.029, 435 2203.479 C 294.670 2304.720, 182.531 2437.021, 106.958 2590.500 C 96.734 2611.263, 80 2647.926, 80 2649.562 C 80 2649.863, 79.123 2652.034, 78.052 2654.385 C 56.867 2700.889, 25.172 2809.048, 16.626 2864 C 16.326 2865.925, 14.946 2874.025, 13.558 2882 C 11.224 2895.411, 6.907 2927.410, 5.531 2941.500 C 5.209 2944.800, 4.747 2949.300, 4.504 2951.500 C 1.648 2977.393, 0.591 3002.912, 0.547 3047 C 0.494 3100.305, 2.192 3139.679, 5.441 3160.500 C 5.699 3162.150, 6.193 3166.650, 6.539 3170.500 C 18.839 3307.244, 74.512 3468.327, 154.897 3599.756 C 188.421 3654.568, 233.193 3713.137, 278.895 3761.967 C 314.463 3799.969, 343.832 3827.440, 387.440 3863.500 C 411.279 3883.212, 469.682 3925.061, 486 3934.124 C 487.925 3935.193, 491.388 3937.290, 493.696 3938.784 C 499.195 3942.344, 528.177 3959.475, 539 3965.564 C 551.580 3972.640, 597.702 3995.772, 611.500 4001.925 C 618.100 4004.868, 625.928 4008.363, 628.897 4009.691 C 685.539 4035.045, 764.565 4059.906, 835 4074.530 C 865.994 4080.965, 912.948 4088.264, 937.500 4090.463 C 941.350 4090.808, 945.850 4091.280, 947.500 4091.512 C 949.150 4091.744, 954.100 4092.191, 958.500 4092.505 C 962.900 4092.819, 968.750 4093.286, 971.500 4093.543 C 1009.251 4097.074, 1086.199 4096.809, 1133 4092.988 C 1173.166 4089.708, 1223.081 4082.782, 1256 4075.921 C 1261.225 4074.832, 1270 4073.016, 1275.500 4071.886 C 1315.646 4063.635, 1370.931 4047.348, 1420.500 4029.168 C 1430.351 4025.554, 1463.167 4012.210, 1472 4008.225 C 1493.257 3998.635, 1498.570 3996.159, 1515.500 3987.947 C 1535.129 3978.426, 1559.853 3965.125, 1580.500 3952.979 C 1624.875 3926.875, 1667.085 3897.832, 1706 3866.625 C 1716.730 3858.020, 1737.458 3840.692, 1739 3839.037 C 1739.275 3838.741, 1741.525 3836.739, 1744 3834.587 C 1757.379 3822.952, 1772.437 3808.700, 1798.452 3783.048 C 1825.455 3756.422, 1852.107 3729.986, 2029 3554.379 C 2066.125 3517.523, 2127.078 3457.023, 2164.451 3419.935 C 2201.823 3382.846, 2262.573 3322.548, 2299.451 3285.940 C 2336.328 3249.333, 2397.301 3188.808, 2434.948 3151.440 C 2500.288 3086.584, 2553.953 3033.312, 2705.500 2882.867 C 2906.279 2683.550, 2897.626 2692.074, 2911.529 2679.903 C 2971.793 2627.148, 3042.886 2590.599, 3122.500 2571.443 C 3148.639 2565.153, 3167.986 2562.125, 3202 2558.999 C 3211.523 2558.124, 3274.304 2558.387, 3279 2559.321 C 3280.375 2559.595, 3284.875 2560.114, 3289 2560.475 C 3485.334 2577.638, 3659.521 2722.306, 3714.660 2914 C 3768.924 3102.649, 3709.269 3299.147, 3558.247 3429.216 C 3527.414 3455.771, 3475.081 3488.267, 3438 3503.883 C 3420.985 3511.049, 3396.730 3520.171, 3388.727 3522.415 C 3353.267 3532.357, 3333.402 3536.945, 3315 3539.444 C 3303.374 3541.023, 3295.241 3542.118, 3292.500 3542.475 C 3198.827 3554.663, 3080.627 3530.729, 2995 3482.234 C 2975.378 3471.121, 2928.656 3439.426, 2924 3434.070 C 2923.725 3433.753, 2919 3429.617, 2913.500 3424.877 C 2908 3420.138, 2902.150 3415.020, 2900.500 3413.505 C 2895.794 3409.183, 2767.045 3281.788, 2673.500 3188.891 L 2640.500 3156.120 2604.822 3191.310 C 2562.867 3232.691, 2383.442 3410.681, 2304.002 3489.724 L 2248.500 3544.947 2278.500 3574.703 C 2295 3591.069, 2336.625 3632.374, 2371 3666.492 C 2484.666 3779.309, 2520.399 3814.485, 2529.517 3822.537 C 2534.476 3826.917, 2541.853 3833.425, 2545.909 3837 C 2549.965 3840.575, 2553.782 3843.970, 2554.392 3844.543 C 2555.001 3845.117, 2559.775 3849.103, 2565 3853.401 C 2570.225 3857.699, 2575.400 3861.962, 2576.500 3862.874 C 2584.428 3869.446, 2599.741 3881.278, 2612 3890.303 C 2619.975 3896.175, 2627.093 3901.480, 2627.818 3902.092 C 2629.730 3903.708, 2679.865 3936.863, 2686.651 3941 C 2692.291 3944.438, 2696.010 3946.662, 2705.655 3952.364 C 2721.222 3961.567, 2749.440 3976.716, 2769.022 3986.382 C 2790.482 3996.976, 2796.116 3999.647, 2809.250 4005.457 C 2812.413 4006.856, 2817.388 4009.079, 2820.308 4010.398 C 2844.371 4021.272, 2875.740 4032.718, 2931.606 4051.008 C 2944.504 4055.231, 2972.302 4062.579, 3001.500 4069.484 C 3017.100 4073.174, 3056.790 4080.955, 3070 4082.914 C 3073.575 4083.444, 3078.300 4084.146, 3080.500 4084.474 C 3102.256 4087.719, 3107.040 4088.324, 3128.085 4090.498 C 3178.006 4095.653, 3264.473 4097.435, 3307.500 4094.195 C 3338.348 4091.872, 3376.381 4087.722, 3397 4084.430 C 3402.225 4083.596, 3408.020 4082.671, 3409.877 4082.375 C 3411.734 4082.079, 3414.191 4081.656, 3415.337 4081.435 C 3416.482 4081.215, 3419.238 4080.779, 3421.460 4080.467 C 3423.682 4080.155, 3426.175 4079.713, 3427 4079.486 C 3427.825 4079.258, 3430.075 4078.807, 3432 4078.484 C 3501.541 4066.811, 3600.473 4036.175, 3667.500 4005.557 C 3671.350 4003.798, 3677.425 4001.048, 3681 3999.446 C 3689.562 3995.609, 3732.578 3974.112, 3741.500 3969.212 C 3796.815 3938.830, 3845.090 3906.927, 3891.500 3870.079 C 3902.388 3861.435, 3920.761 3846.344, 3922 3845.028 C 3922.275 3844.736, 3924.300 3842.968, 3926.500 3841.099 C 4029.862 3753.305, 4122.035 3633.811, 4183.056 3508.500 C 4194.991 3483.990, 4201.321 3469.862, 4213.524 3440.500 C 4252.794 3346.006, 4280.432 3222.140, 4286.193 3114.818 C 4286.862 3102.342, 4287.768 3092.356, 4288.205 3092.627 C 4288.642 3092.897, 4289 3074.615, 4289 3052 C 4289 3029.385, 4288.620 3011.117, 4288.155 3011.404 C 4287.690 3011.692, 4287.009 3004.631, 4286.642 2995.713 C 4282.684 2899.624, 4256.586 2772.570, 4223.574 2688.676 C 4222.238 2685.279, 4218.688 2676.200, 4215.686 2668.500 C 4169.728 2550.613, 4100.991 2441.757, 4009.562 2342.065 C 3992.082 2323.006, 3951.053 2283.266, 3931 2265.971 C 3926.875 2262.413, 3923.275 2259.250, 3923 2258.941 C 3919.874 2255.434, 3883.311 2226.340, 3865 2212.790 C 3857.575 2207.295, 3850.375 2201.957, 3849 2200.928 C 3808.670 2170.748, 3720.960 2120.520, 3671 2098.996 C 3666.875 2097.219, 3661.700 2094.963, 3659.500 2093.983 C 3653.165 2091.160, 3632.129 2082.471, 3625 2079.733 C 3621.425 2078.359, 3613.775 2075.425, 3608 2073.213 C 3583.931 2063.992, 3533.969 2048.123, 3508 2041.451 C 3484.457 2035.403, 3454.159 2028.629, 3434.500 2025.019 C 3407.283 2020.022, 3386.650 2016.875, 3365.500 2014.498 C 3350.576 2012.820, 3344.607 2012.141, 3339 2011.485 C 3268.336 2003.215, 3139.041 2007.506, 3069 2020.446 C 3067.625 2020.700, 3061.100 2021.824, 3054.500 2022.944 C 3016.294 2029.428, 2967.453 2041.097, 2930 2052.691 C 2888.012 2065.688, 2840.890 2083.158, 2814.158 2095.638 C 2809.019 2098.037, 2804.597 2100, 2804.329 2100 C 2800.461 2100, 2726.254 2138.349, 2705 2151.332 C 2701.425 2153.515, 2696.475 2156.425, 2694 2157.797 C 2691.525 2159.169, 2686.350 2162.389, 2682.500 2164.952 C 2678.650 2167.516, 2674.825 2169.915, 2674 2170.284 C 2671.057 2171.599, 2636.804 2194.711, 2624.180 2203.900 C 2605.475 2217.515, 2566.576 2247.820, 2560.777 2253.295 C 2560.075 2253.958, 2556.575 2256.954, 2553 2259.952 C 2549.425 2262.951, 2543.555 2268.126, 2539.955 2271.452 C 2536.356 2274.779, 2528.706 2281.765, 2522.955 2286.978 C 2517.205 2292.191, 2489.550 2319.218, 2461.500 2347.038 C 2352.812 2454.837, 2262.951 2544.005, 2202.500 2604.043 C 2167.850 2638.455, 2106.875 2698.985, 2067 2738.554 C 2027.125 2778.122, 1964.350 2840.431, 1927.500 2877.018 C 1890.650 2913.604, 1829.675 2974.140, 1792 3011.540 C 1754.325 3048.941, 1695.150 3107.690, 1660.500 3142.093 C 1625.850 3176.496, 1564.875 3237.005, 1525 3276.556 C 1485.125 3316.108, 1446.425 3354.504, 1439 3361.881 C 1376.037 3424.438, 1361.104 3437.655, 1328 3460.125 C 1318.144 3466.815, 1306.327 3474.315, 1301.332 3477.051 C 1299.774 3477.904, 1293.957 3481.153, 1288.405 3484.270 C 1231.629 3516.150, 1161.262 3537.485, 1093 3543.516 C 1089.425 3543.832, 1084.250 3544.299, 1081.500 3544.555 C 1010.688 3551.143, 913.543 3533.407, 842.161 3500.858 C 810.593 3486.464, 771.206 3462.735, 745.007 3442.326 C 600.009 3329.375, 530.231 3149.767, 560.539 2967.500 C 571.934 2898.975, 600.819 2828.494, 641.276 2770.500 C 814.143 2522.698, 1168.673 2485.645, 1389 2692.352 C 1398.849 2701.592, 1469.286 2771.107, 1600.787 2901.367 L 1647.073 2947.217 1691.787 2902.785 C 1716.379 2878.348, 1760.562 2834.537, 1789.971 2805.427 C 1900.554 2695.970, 1944.864 2652.055, 1991.500 2605.698 L 2039.500 2557.984 2001.500 2520.305 C 1936.917 2456.267, 1814.253 2335.190, 1788.440 2310 C 1766.384 2288.477, 1756.678 2279.394, 1741.940 2266.487 C 1735.711 2261.032, 1718.560 2246.481, 1715.397 2243.966 C 1713.690 2242.610, 1708.065 2238.098, 1702.897 2233.939 C 1694.338 2227.052, 1669.077 2207.953, 1663 2203.775 C 1661.625 2202.830, 1658.250 2200.442, 1655.500 2198.470 C 1639.213 2186.790, 1611.222 2168.574, 1589.500 2155.520 C 1517.247 2112.097, 1432.214 2074.701, 1352 2051.072 C 1346.225 2049.370, 1340.150 2047.546, 1338.500 2047.017 C 1334.606 2045.768, 1288.718 2034.102, 1283.500 2033.034 C 1280.232 2032.365, 1254.159 2026.967, 1246.500 2025.374 C 1243.580 2024.766, 1217.102 2020.189, 1213 2019.583 C 1176.433 2014.178, 1147.527 2010.977, 1121.500 2009.449 C 1116 2009.126, 1110.150 2008.642, 1108.500 2008.373 C 1102.612 2007.415, 1011.214 2007.175, 996 2008.078"
                            stroke="none"
                            fill="currentColor"
                            fill-rule="evenodd"
                        ></path>
                    </svg>

                    Earn with allmyonline
                </button>
            </div>
        </div>

        <!-- QR code, fixed bottom-right -->
        <div class="qr-container">
            <qrcode-vue
                :value="urlProfile"
                size="70"
                level="H"
                style="width: 130px; height: 130px"
            />
            <p>Scan to view on mobile</p>
        </div>
    </div>

    <SharePopup
        :show="showPopup"
        :username="user.username"
        :user="user"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
        :shortCode="shareCodeForPopup"
        :data="data"
        :referral_code="user.referral_code"
        @close="showPopup = false"
    />
    <video-popup
        :data="modal.data"
        :display_options_video="modal.display_options_video"
        ref="setShowVideoTrue"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <modal-link-shopify
        v-for="item in links"
        :key="'shopify-list-' + item.id"
        :linkId="item.id"
        :modalId="'shopify-list-' + item.id"
        :item="item"
        :shopify="shopify"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <CalendlyEmbed
        ref="calendlyRef"
        :username="user.username"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <MusicEmbedModal
        ref="embedModalRef"
        :username="user.username"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <TypeFormEmbed
        ref="typeformRef"
        :username="user.username"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <XEmbed
        ref="xRef"
        :urlName="urlName"
        :username="user.username"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <TikTokPopup
        ref="tiktokModalRef"
        :username="user.username"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <TwitchEmbed
        ref="twitchEmbedRef"
        :urlName="urlName"
        :username="user.username"
    />
    <contact-details-view-in-profile
        ref="contactDetailsRef"
        :username="user.username"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
    />
    <contact-form-view-in-profile
        :country="country"
        ref="contactFormRef"
        :username="user.username"
        :urlName="urlName"
        :DoBucket="DoBucket"
        :DoEndpoint="DoEndpoint"
        :data="data"
    />

    <teleport to="body">
        <FeedbackModal
            ref="feedbackModal"
            :loading="isLoadingFeedback"
            prompt="What did you think of this profile?"
            @submitted="submitProfileFeedback"
        />
    </teleport>

    <!-- Reusable BootstrapModal instance for unlock flow -->
    <teleport to="body">
        <BootstrapModal
            ref="bootstrapModal"
            modalId="unlockModal"
            :title="unlockModalData?.title || 'Unlock required'"
            :loading="false"
            modalSize="l"
        >
            <template #body>
                <div>
                    <!-- Step 1: Code required form -->
                    <div
                        v-if="
                            !isFreePlan &&
                            (unlockModalData?.step === 1 ||
                                unlockModalData?.reason === 'code_required')
                        "
                    >
                        <CodeUnlockForm
                            :link-id="unlockModalData?.link?.id"
                            @success="onCodeUnlockSuccess"
                        />
                    </div>

                    <!-- Step 2: Age verification required -->
                    <div
                        v-else-if="
                            unlockModalData?.step === 2 ||
                            unlockModalData?.reason ===
                                'age_verification_required'
                        "
                    >
                        <AgeVerification
                            :link-id="unlockModalData?.link?.id"
                            :min-age="unlockModalData?.locks?.age"
                            :description="
                                unlockModalData?.locks?.description_age_lock
                            "
                            @success="onCodeUnlockSuccess"
                        />
                    </div>

                    <!-- Step 2: Age verification required -->
                    <div
                        v-else-if="
                            unlockModalData?.step === 3 ||
                            unlockModalData?.reason ===
                                'sensitive_content_required'
                        "
                    >
                        <SensitiveContent
                            :link-id="unlockModalData?.link?.id"
                            :description="
                                unlockModalData?.locks
                                    ?.description_sensitive_content
                            "
                            @success="onCodeUnlockSuccess"
                        />
                    </div>

                    <!-- Default / other steps info -->
                    <template v-else>
                        <p v-if="unlockModalData && unlockModalData.reason">
                            {{ unlockModalData.reason }}
                        </p>
                        <div v-else>
                            <p>Please complete the required step to proceed.</p>
                        </div>
                    </template>
                </div>
            </template>
        </BootstrapModal>
    </teleport>
</template>

<script>
import "@/assets/css/mobile.css";
import "@/assets/css/theme.min.css";
import "@/assets/js/theme.min.js";
import "@/assets/vendor/bootstrap/dist/js/bootstrap.bundle.min.js";
import "@/assets/vendor/smooth-scroll/dist/smooth-scroll.polyfills.min.js";
import AgeVerification from "@/Components/AgeVerification.vue";
import AnimatedGlow from "@/Components/AnimatedGlow.vue";
import AppButton from "@/Components/AppButton.vue";
import BootstrapModal from "@/Components/BootstrapModal.vue";
import BorderBottomCurve from "@/Components/BorderBottomCurve.vue";
import BorderTopCurve from "@/Components/BorderTopCurve.vue";
import BorderWaveBottom from "@/Components/BorderWaveBottom.vue";
import BorderWaveTop from "@/Components/BorderWaveTop.vue";
import CollectEmailsForm from "@/Components/CollectEmails/CollectEmailsForm.vue";
import FeedbackModal from "@/Components/FeedbackModal.vue";
import FlatInput from "@/Components/FlatInput.vue";
import FollowerStatsDisplay from "@/Components/FollowerStats/FollowerStatsDisplay.vue";
import ProfileIdentity from "@/Components/UserProfile/ProfileIdentity.vue";
import LogoAllMyOnline from "@/Components/Icons/LogoAllMyOnline.vue";
import ImpersonationBar from "@/Components/ImpersonationBar.vue";
import CalendlyEmbed from "@/Components/Links/Calendly/CalendlyEmbed.vue";
import ContactDetailsViewInProfile from "@/Components/Links/ContactDetailsViewInProfile.vue";
import ContactFormViewInProfile from "@/Components/Links/ContactFormViewInProfile.vue";
import GeneralButton from "@/Components/Links/GeneralButton.vue";
import CodeUnlockForm from "@/Components/Links/Lock/CodeUnlockForm.vue";
import SensitiveContent from "@/Components/Links/Lock/SensitiveContent.vue";
import UnLock from "@/Components/Links/Lock/UnLock/UnLock.vue";
import ModalLinkShopify from "@/Components/Links/ModalLinkshopify.vue";
import MusicEmbedModal from "@/Components/Links/Music/MusicEmbedModal.vue";
import CalendlyCard from "@/Components/Links/Platforms/Calendly/CalendlyCard.vue";
import ContactDetailsCard from "@/Components/Links/Platforms/ContactDetails/ContactDetailsCard.vue";
import ContactFormCard from "@/Components/Links/Platforms/ContactForm/ContactFormCard.vue";
import DeezerCard from "@/Components/Links/Platforms/Deezer/DeezerCard.vue";
import FacebookCard from "@/Components/Links/Platforms/Facebook/FacebookCard.vue";
import GoogleMapCard from "@/Components/Links/Platforms/GoogleMaps/GoogleMapCard.vue";
import ShopCard from "@/Components/Links/Platforms/Shop/ShopCard.vue";
import ShopifyCard from "@/Components/Links/Platforms/Shopify/ShopifyCard.vue";
import SoundCloudCard from "@/Components/Links/Platforms/SoundCloud/SoundCloudCard.vue";
import TikTokCard from "@/Components/Links/Platforms/TikTok/TikTokCard.vue";
import TwitchCard from "@/Components/Links/Platforms/Twitch/TwitchCard.vue";
import TypeformCard from "@/Components/Links/Platforms/Typeform/TypeformCard.vue";
import XCard from "@/Components/Links/Platforms/X/XCard.vue";
import YouTubeCard from "@/Components/Links/Platforms/YouTube/YouTubeCard.vue";
import TikTokPopup from "@/Components/Links/TikTok/TikTokPopup.vue";
import TwitchEmbed from "@/Components/Links/Twitch/TwitchEmbed.vue";
import TypeFormEmbed from "@/Components/Links/TypeForm/TypeFormEmbed.vue";
import VideoPopup from "@/Components/Links/Video/VideoPopup.vue";
import XEmbed from "@/Components/Links/X/XEmbed.vue";
import Logo from "@/Components/Logo.vue";
import MRegisterPopup from "@/Components/MRegisterPopup.vue";
import SharePopup from "@/Components/SharePopup.vue";
import { getSvgForPlatform } from "@/services/iconService.js";
import { buildButtonStyle } from "@/utils/buttonStyle";
import { buildProfileCardStyle } from "@/utils/profileAppearance";
import { resendEmailVerificationRequest } from "@/utils/resendEmailVerification";
import "@assets/css/public-profile.css";
import { IconShare } from "@tabler/icons-vue";
import "bootstrap/dist/css/bootstrap.css";
import { container } from "jenesius-vue-modal";
import "keen-slider/keen-slider.min.css";
import QRCode from "qrcode";
import QrcodeVue from "qrcode.vue";
import { Carousel, Navigation, Pagination, Slide } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";
import { toast } from "vue3-toastify";
export default {
    name: "PublicProfile",
    components: {
        AnimatedGlow,
        Carousel,
        Slide,
        Pagination,
        Navigation,
        GoogleMapCard,
        FacebookCard,
        CalendlyCard,
        GeneralButton,
        MRegisterPopup,
        QrcodeVue,
        SharePopup,
        Logo,
        LogoAllMyOnline,
        IconShare,
        WidgetContainerModal: container,
        UnLock,
        ModalLinkShopify,
        VideoPopup,
        CalendlyEmbed,
        MusicEmbedModal,
        TypeFormEmbed,
        XEmbed,
        TikTokPopup,
        TwitchEmbed,
        BorderWaveBottom,
        BorderWaveTop,
        BorderBottomCurve,
        BorderTopCurve,
        ShopCard,
        ContactDetailsViewInProfile,
        ContactFormViewInProfile,
        AppButton,
        FlatInput,
        FeedbackModal,
        FollowerStatsDisplay,
        ProfileIdentity,
        YouTubeCard,
        TikTokCard,
        ShopifyCard,
        SoundCloudCard,
        TwitchCard,
        DeezerCard,
        TypeformCard,
        XCard,
        ContactDetailsCard,
        ContactFormCard,
        ImpersonationBar,
        BootstrapModal,
        CodeUnlockForm,
        AgeVerification,
        SensitiveContent,
        CollectEmailsForm,
    },
    props: {
        links: [],
        user: Object,
        appearance: { type: Object, required: true },
        urlName: String,
        shopify: [],
        country: [],
        iconSelected: [],
        getCollectionsWithProducts: [],
        products: [],
        DoBucket: "",
        DoEndpoint: "",
        impersonation: {
            type: Object,
            default: () => ({}),
        },
        secondaryLink: {
            type: Object,
            default: () => ({
                href: "#",
                text: "",
            }),
        },
        collectEmailsSettings: {
            type: Object,
            default: () => ({}),
        },
        followerStatsSettings: {
            type: Object,
            default: () => ({}),
        },
        currentPageId: {
            type: Number,
            default: 1,
        },
        previewMode: {
            type: Boolean,
            default: false,
        },
        screenshotMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            data: {},
            shareCodeForPopup: null,
            showPopup: false,
            showMRegister: false,
            shopifyBreakpoints: {
                // For mobile, show 1.5 items to hint at scrollability
                0: { itemsToShow: 1.5, snapAlign: "start" },
                // Revert to original settings for tablet and desktop
                768: { itemsToShow: 3 },
                1024: { itemsToShow: 2 },
            },
            breakpoints: {
                0: { slidesPerView: 3.5, spaceBetween: 5 }, // Small screens
                320: { slidesPerView: 4.5, spaceBetween: 5 }, // Slightly larger screens
                480: { slidesPerView: 4.5, spaceBetween: 8 },
                768: { slidesPerView: 6.5, spaceBetween: 10 },
                1024: {
                    slidesPerView: 7,
                    spaceBetween: 12,
                    allowTouchMove: false,
                },
            },
            activeTab: "links",
            run_slider: true,
            slider_social: true,
            urlNameImage: this.urlName,
            stageProps: 0,
            setUnlockActive: "",
            val: 3, // default value,
            classType: "col-lg-4",
            isMobile: false,
            setShowVideoTrue: [],
            modal: {
                data: {},
                display_options_video: {},
            },
            isLoadingFeedback: false,
            unlockModalData: null,
            showUnlockModal: false,
            accessCode: "",
            // Track which steps a visitor has already unlocked per link id
            unlockedStepsByLink: {},
            // Remember the original user action that triggered the lock
            pendingAction: null,
            isResendingEmailVerification: false,
            emailVerificationEmailSent: false,
        };
    },

    mounted() {
        QRCode.toCanvas(this.$refs.qrCanvas, this.urlName, {
            width: 128,
            margin: 2,
        }).catch((err) => console.error(err));
        this.pickBgColorAndApply();
        this.updateText(); // Set initial value
        window.addEventListener("resize", this.updateText);

        this.checkHash();
        // Ensure swipers compute sizes after initial render
        this.updateSwipers();

        // After initial render, apply Spotlight behavior if any link requests it
        this.$nextTick(() => {
            const plan = this.$page?.props?.auth?.user?.plan?.toLowerCase?.();
            if (["pro", "premium"].includes(plan)) {
                this.applySpotlightOnLoad();
            }
        });

        // Detect if page is in iframe and add class for custom scrollbar
        if (window.self !== window.top) {
            document.documentElement.classList.add("in-iframe");
        }

    },
    beforeUnmount() {
        window.removeEventListener("resize", this.updateText);
    },
    watch: {
        activeTab(newVal, oldVal) {
            if (newVal === "links") {
                this.updateSwipers();
            }
        },
        iconSelected: {
            handler() {
                this.updateSwipers();
            },
            deep: true,
        },
    },
    computed: {
        cssFontWeight() {
            const w = this.appearance?.font_weight;
            if (w === 'bold') return 700;
            if (w === 'medium' || w === 'normal' || !w) return 500;
            return w;
        },
        urlProfile() {
            return `${this.urlName}${encodeURIComponent(this.user.username)}`;
        },
        isFreePlan() {
            const plan =
                (this.user && this.user.plan) ||
                (this.$page &&
                    this.$page.props &&
                    this.$page.props.auth &&
                    this.$page.props.auth.user &&
                    this.$page.props.auth.user.plan) ||
                null;
            return plan === "free";
        },
        isEmailVerified() {
            const authUser = this.$page?.props?.auth?.user || {};
            const profileUser = this.user || {};
            const hasVerificationField =
                authUser.email_verified_at !== undefined ||
                authUser.email_verified !== undefined ||
                profileUser.email_verified_at !== undefined ||
                profileUser.email_verified !== undefined;

            if (!hasVerificationField) {
                return true;
            }

            return Boolean(
                authUser.email_verified_at ||
                    authUser.email_verified ||
                    profileUser.email_verified_at ||
                    profileUser.email_verified
            );
        },
        showEmailVerificationBanner() {
            return !this.isEmailVerified && !this.previewMode;
        },
        isTrackingDisabled() {
            return this.previewMode || this.screenshotMode;
        },
        profileCardStyle() {
            return buildProfileCardStyle(
                this.appearance,
                this.urlName,
                window.innerWidth
            );
        },
        iconColorType() {
            return this.appearance.icon_color_type || "color";
        },
        socialIconsColor() {
            let socialIconsColor = "";
            // When transparent style + mono color, use button background for contrast
            if (
                this.appearance.icon_style === "style3" &&
                this.iconColorType === "mono"
            ) {
                socialIconsColor = this.appearance.background_social_icon;
            } else {
                socialIconsColor = this.appearance.color_social_icon;
            }
            if (socialIconsColor && socialIconsColor.length > 0) {
                return socialIconsColor;
            }
            return "#000000";
        },
        // Limit visible social icons on Free plan to the first two enabled ones
        visibleSocialIcons() {
            return Array.isArray(this.iconSelected)
                ? this.iconSelected.filter(
                      (i) =>
                          i && (i.enabled === true || (i.value && i.svg_icon))
                  )
                : [];
        },
        buttonStyle() {
            const overrides = { padding: "0px" };
            // In free plan, do not apply box shadow to buttons
            if (this.isFreePlan) {
                this.appearance.button_type = "fill";
                overrides.boxShadow = "none";
            }
            return buildButtonStyle(this.appearance, overrides);
        },
        // Compute dynamic styles
        items() {
            return this.links;
        },
        hasAnyLinks() {
            return (
                (Array.isArray(this.links) && this.links.length > 0) ||
                this.collectEmailsSettings?.active === true ||
                this.shouldShowFollowerStats
            );
        },
        shouldShowFollowerStats() {
            return (
                this.followerStatsSettings?.active === true &&
                this.followerStatsSettings?.has_accounts === true &&
                Number(this.followerStatsSettings?.total_followers || 0) > 0
            );
        },
        impersonationState() {
            return this.impersonation || {};
        },
        impersonationWithTargetLabel() {
            const state = this.impersonationState;
            const targetLabel =
                this.user?.name ||
                (this.user?.username ? `@${this.user.username}` : "this user");

            return {
                ...state,
                targetLabel,
            };
        },
        impersonationMessage() {
            const state = this.impersonationState;

            if (state.isImpersonating) {
                const targetName =
                    state.impersonated?.name ||
                    (state.impersonated?.username
                        ? `@${state.impersonated.username}`
                        : "this account");

                return `You are currently impersonating ${targetName}.`;
            }

            if (state.canImpersonateTarget) {
                const profileLabel =
                    this.user?.name ||
                    (this.user?.username
                        ? `@${this.user.username}`
                        : "this user");

                return `Admin`;
            }

            return "";
        },
        showImpersonateButton() {
            return (
                Boolean(this.impersonationState.canImpersonateTarget) &&
                !this.impersonationState.isImpersonating
            );
        },
        showLeaveButton() {
            return Boolean(this.impersonationState.isImpersonating);
        },
        impersonationButtonLabel() {
            return (
                this.user?.name ||
                (this.user?.username ? `@${this.user.username}` : "this user")
            );
        },
        normalizedTopIconStyle() {
            return this.appearance?.top_icon_style;
        },
        // Style for the header/settings icon based on appearance.top_icon_style
        headerIconStyle() {
            const s = this.normalizedTopIconStyle;
            // Explicit mappings
            if (s === "white") return { color: "#fff" };
            if (s === "black") return { color: "#000" };
            if (s === "white_on_black")
                return {
                    color: "#fff",
                    backgroundColor: "#000",
                    borderRadius: "9999px",
                };
            if (s === "black_on_white")
                return {
                    color: "#000",
                    backgroundColor: "#fff",
                    borderRadius: "9999px",
                };
            // Fallback to theme button color if provided, otherwise inherit
            const fallback = this.appearance?.button_color;
            return fallback ? { color: fallback } : {};
        },
        // Whether the header icon should render with a rounded background
        headerIconHasBackground() {
            const s = this.normalizedTopIconStyle;
            return s === "white_on_black" || s === "black_on_white";
        },
    },
    methods: {
        async resendEmailVerification() {
            if (this.previewMode) {
                return;
            }
            this.isResendingEmailVerification = true;
            try {
                await resendEmailVerificationRequest();
                this.emailVerificationEmailSent = true;
            } catch (_) {
                alert("Could not resend verification email. Please try again.");
            } finally {
                this.isResendingEmailVerification = false;
            }
        },
        // Safely force Swiper instances to recalculate sizes after DOM updates.
        // We keep this defensive: if no Swiper is present, it's a no-op to avoid runtime errors.
        updateSwipers() {
            // Defer until DOM updates are flushed
            this.$nextTick(() => {
                try {
                    // Swiper v7+ attaches the instance to the root element as `el.swiper` in Vue integration
                    const nodes = document.querySelectorAll(".swiper");
                    nodes.forEach((el) => {
                        const instance = el && el.swiper;
                        if (instance && typeof instance.update === "function") {
                            instance.update();
                        }
                    });
                    // Fallback: trigger a resize event so responsive components recalc
                    if (typeof window !== "undefined") {
                        window.dispatchEvent(new Event("resize"));
                    }
                } catch (e) {
                    // swallow to prevent UI crash; we only aim to best-effort update
                    // console.debug('updateSwipers failed', e);
                }
            });
        },
        // Compute the next step to display based on locks and which steps are already unlocked for this link
        computeNextStep(locks, unlocked = {}) {
            if (!locks || typeof locks !== "object") return null;
            // Step 1: Code lock — skip for free plan (free users should not see code form)
            if (
                !this.isFreePlan &&
                locks.code_lock_status &&
                locks.code &&
                !unlocked[1]
            ) {
                return 1;
            }
            // Step 2: Age verification
            if (locks.date_birth_status && locks.age && !unlocked[2]) {
                return 2;
            }
            // Step 3: Sensitive content
            if (locks.sensitive_content_lock_status && !unlocked[3]) {
                return 3;
            }
            return null;
        },
        // Mark a given step as unlocked for a specific link id
        markUnlocked(linkId, step) {
            if (!linkId || !step) return;
            if (!this.unlockedStepsByLink[linkId]) {
                this.$set
                    ? this.$set(this.unlockedStepsByLink, linkId, {})
                    : (this.unlockedStepsByLink[linkId] = {});
            }
            this.$set
                ? this.$set(this.unlockedStepsByLink[linkId], step, true)
                : (this.unlockedStepsByLink[linkId][step] = true);
        },
        socialIconHref(icon) {
            const href = String(icon?.display_url || icon?.value || "").trim();
            if (/^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(href)) {
                return href;
            }

            try {
                const url = new URL(href);
                return ["http:", "https:"].includes(url.protocol) ? href : "#";
            } catch {
                return "#";
            }
        },
        goSignup() {
            const host = (
                this.urlName ||
                window.location?.origin ||
                "http://localhost:8000"
            )
                .toString()
                .replace(/\/+$/, "");
            const handle = (this.user.username || "")
                .trim()
                .replace(/\s+/g, "")
                .toLowerCase();

            const refFromUrl =
                typeof window !== "undefined"
                    ? new URLSearchParams(window.location.search).get("ref")
                    : null;
            const ref = this.user.referral_code || refFromUrl;

            let url = `${host}/signup/details?username=${encodeURIComponent(
                handle
            )}`;
            if (ref) url += `&ref=${encodeURIComponent(ref)}`;

            window.location.href = url;
        },
        openMRegisterPopup() {
            this.showMRegister = true;
        },
        closeMRegisterPopup() {
            this.showMRegister = false;
        },
        openSharePopup(link = null) {
            this.shareCodeForPopup = link?.short_code || null;
            this.data = link || null;
            this.showPopup = true;
        },
        // On initial page load, if any link has prioritize.type === 'spotlight',
        // add the 'spotlight' class to its button and auto-open it by triggering click.
        applySpotlightOnLoad() {
            try {
                if (!Array.isArray(this.links) || this.links.length === 0)
                    return;
                // Find first spotlight link
                const spotlightLink = this.links.find(
                    (l) => l?.options?.prioritize?.type === "spotlight"
                );
                if (!spotlightLink) return;
                // Wait a tick to ensure the DOM for the button is mounted
                this.$nextTick(() => {
                    const el = document.getElementById(
                        String(spotlightLink.id)
                    );
                    if (!el) return;
                    // Ensure class is present even if component didn't bind it
                    el.classList.add("spotlight");
                    // Trigger a click to open corresponding modal/interaction
                    try {
                        el.click();
                    } catch (e) {}
                });
            } catch (e) {
                // Silent fail to avoid disrupting the page
            }
        },
        handleGeneralButtonClick(link) {
            // For generic buttons (v-else), open the target URL directly and do not open any modal
            if (!link || !link.url) return;
            if (this.isTrackingDisabled) {
                this.openExternalLink(link.url);
                return;
            }
            return this.performTrackedAction(
                link.url,
                link.user_id,
                link.platform,
                link
            );
        },
        pickBgColorAndApply() {
            // If a desktop background color is already saved in appearance, use it directly
            if (this.appearance && this.appearance.desktop_background_color) {
                const bg = this.appearance.desktop_background_color;

                if (bg) {
                    if (
                        bg.startsWith("linear-gradient") ||
                        bg.startsWith("radial-gradient")
                    ) {
                        document.body.style.setProperty(
                            "background",
                            bg,
                            "important"
                        );

                        const wrapper = document.querySelector(".page-wrapper");
                        if (wrapper) {
                            wrapper.style.setProperty(
                                "background",
                                bg,
                                "important"
                            );
                        }
                    } else {
                        document.body.style.setProperty(
                            "background-color",
                            bg,
                            "important"
                        );

                        const wrapper = document.querySelector(".page-wrapper");
                        if (wrapper) {
                            wrapper.style.setProperty(
                                "background-color",
                                bg,
                                "important"
                            );
                        }
                    }
                }
            }
            // Otherwise, fallback: extract the average center pixel color from the background image
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.src = `${this.urlName}appearance/advance_background/desktop/${this.appearance.background}`;
            img.onload = () => {
                const c = document.createElement("canvas");
                const ctx = c.getContext("2d");
                c.width = c.height = 1;
                // draw center of the full-size image into our 1×1 canvas:
                ctx.drawImage(
                    img,
                    img.width / 2,
                    img.height / 2, // source x,y
                    1,
                    1, // source w,h
                    0,
                    0,
                    1,
                    1 // dest x,y,w,h
                );
                const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
                document.body.style.backgroundColor = `rgb(${r},${g},${b})`;
            };
        },
        openProfileFeedback() {
            if (this.previewMode || !this.user?.id) {
                toast.info("Feedback is unavailable in preview mode.");
                return;
            }
            this.$refs.feedbackModal.open();
        },
        async submitProfileFeedback({ rating, feedback }) {
            if (this.isTrackingDisabled || !this.user?.id) {
                toast.info("Feedback is unavailable in preview mode.");
                return;
            }
            this.isLoadingFeedback = true;
            try {
                await axios.post(
                    route("profile.feedback", { user: this.user.id }),
                    { rating, feedback }
                );
                toast.success("Thank you for your feedback!");
            } catch (err) {
                console.error(err);
                toast.error("Couldn’t send feedback.");
            } finally {
                this.isLoadingFeedback = false;
            }
        },
        getUnstyledSvg(title) {
            return getSvgForPlatform(title);
        },
        getStyledSvg(title) {
            return getSvgForPlatform(title, "mono");
        },
        openExternalLink(url) {
            if (!url) return;
            if (this.previewMode) {
                window.open(url, "_blank", "noopener");
                return;
            }
            window.location.href = url;
        },
        shouldShowSocialIcons() {
            return this.appearance.icon_status;
        },
        isBottomWithIcons() {
            if (!this.shouldShowSocialIcons()) {
                return false;
            }
            return this.appearance.icon_position === "bottom";
        },
        isTopWithIcons() {
            if (!this.shouldShowSocialIcons()) {
                return false;
            }
            return this.appearance.icon_position === "top";
        },
        toggle(selected) {
            this.activeTab = selected; // Update the active tab
        },
        async checkHash() {
            const hash = window.location.hash;
            if (hash) {
                const id = hash.replace("#", "");

                // 1. Find the link by ID from your `links` array
                const link = this.links.find((l) => l.id.toString() === id);

                if (!link) return;

                // 2. Trigger animation
                this.animateButton(id);

                // 3. Delay slightly for visual effect, then handle modal
                setTimeout(async () => {
                    const type = link.platform; // or link.type

                    if (type === "contact-form") {
                        try {
                            await this.$refs.contactFormRef.openContactFormById(
                                link
                            );
                            $j("#contact-form").modal("show");
                        } catch (error) {
                            console.error(
                                "Error opening contact form modal:",
                                error
                            );
                        }
                    } else if (type === "contact-details") {
                        try {
                            await this.$refs.contactDetailsRef.openContactDetailsById(
                                link
                            );
                            $j("#contact-details-form-create").modal("show");
                        } catch (error) {
                            console.error(
                                "Error opening contact details modal:",
                                error
                            );
                        }
                    }

                    // Add more types if needed
                }, 300); // Wait 300ms before showing modal
            }
        },
        animateButton(id) {
            const button = document.getElementById(`${id}`);
            if (button) {
                // Add animation class
                button.classList.add("animate");
                setTimeout(() => {
                    // Remove the animation class after 3 seconds
                    button.classList.remove("animate");
                }, 3000);
            }
        },
        updateText() {
            if (window.innerWidth <= 768) {
                this.val = 1;
                this.classType = "col";
                this.isMobile = true;
            } else {
                this.val = 3;
                this.classType = "col-lg-4";
                this.isMobile = false;
            }
        },
        computeBorderRadius(text) {
            if (text?.includes("rounded-pill") || text?.includes("rounded-5")) {
                return "rounded-img";
            } else if (text?.includes("rounded-1")) {
                return "rounded-1";
            } else if (text?.includes("rounded-left")) {
                return "rounded-img";
            } else {
                return "";
            }
        },
        shouldOpenMusicPopup(link) {
            const music = link.options?.music || {};
            // Backward compatibility with legacy boolean flags
            const legacy =
                (music.streaming_and_preview === true ||
                    music.streaming_only === true ||
                    music.preview_only === true) &&
                music.go_directly !== true;

            // New logic based on display_mode string
            const mode =
                music.display_mode ||
                music.music_option ||
                link.music_option ||
                "streaming-and-preview";
            const openModes = [
                "streaming-and-preview",
                "streaming-only",
                "preview-only",
            ];

            return legacy || openModes.includes(mode);
        },
        shouldOpenTikTokProfileOnly(link) {
            const tiktok = link.options?.tiktok || {};
            return tiktok.display_mode === "profile_only";
        },
        shouldOpenTikTokWithVideos(link) {
            const tiktok = link.options?.tiktok || {};
            return tiktok.display_mode === "profile_and_videos";
        },
        /**
         * Try to open the reusable BootstrapModal with consistent payload.
         * If the BootstrapModal component exposes a custom `open` method use it,
         * otherwise fall back to existing jQuery modal usage per-link.
         */
        openBootstrapUnlockModal(link, payload) {
            // Figure out which step we should show, considering already unlocked steps for this link
            const unlocked = this.unlockedStepsByLink[link.id] || {};
            let stepToShow = payload?.step || null;
            const locks = payload?.locks || null;

            // If we have full lock info, compute the next pending step client-side
            if (locks) {
                stepToShow = this.computeNextStep(locks, unlocked);
            }

            // Free plan behavior: skip code-required step entirely and jump to the next applicable step.
            if (this.isFreePlan) {
                // If server indicated step 1 or our computation would have been step 1, try to skip it
                if (stepToShow === 1) {
                    if (locks) {
                        // Recompute pretending code lock is off
                        const locksWithoutCode = {
                            ...locks,
                            code_lock_status: false,
                        };
                        stepToShow = this.computeNextStep(
                            locksWithoutCode,
                            unlocked
                        );
                    } else {
                        stepToShow = null;
                    }
                }
                // If after skipping code there is no other step, resume the pending action and do not open the modal
                if (!stepToShow) {
                    if (this.pendingAction) {
                        const {
                            url,
                            user_id,
                            type,
                            link: pendingLink,
                        } = this.pendingAction;
                        this.pendingAction = null;

                        // Resume original action after unlock via centralized helper
                        return this.performTrackedAction(
                            url,
                            user_id,
                            type,
                            pendingLink
                        );
                    }
                    return; // nothing to show
                }
            }

            // Determine the appropriate title based on the step
            const step = stepToShow || payload?.step || null;
            let title = "Unlock required";
            if (step === 3) {
                title = "Content Warning";
            }

            this.unlockModalData = {
                link,
                ...payload,
                step,
                title,
            };

            // Use BootstrapModal ref API to open by id (aligns with showModal usage)
            const modalRef = this.$refs.bootstrapModal;
            if (modalRef && typeof modalRef.showModal === "function") {
                modalRef.showModal("unlockModal");
                return;
            }

            // Fallback: if per-link jQuery modal markup exists, show it
            try {
                $j("#unlock-form-" + link.id).modal("show");
            } catch (e) {
                // last-resort: set a reactive flag (component implementation may read this)
                this.showUnlockModal = true;
            }
        },
        submitAccessCode() {
            // Minimal requirement: on submit, trigger the same modal open logic.
            const link = this.unlockModalData?.link || {};
            this.openBootstrapUnlockModal(link, {
                ...this.unlockModalData,
                entered_code: this.accessCode || "",
            });
        },
        onCodeUnlockSuccess(ok) {
            if (!ok) return;

            const link = this.unlockModalData?.link || null;
            const currentStep = this.unlockModalData?.step || null;
            const locks = this.unlockModalData?.locks || {};

            if (link && currentStep) {
                this.markUnlocked(link.id, currentStep);
            }

            // Decide next step using current locks + unlocked flags
            const unlocked = link
                ? this.unlockedStepsByLink[link.id] || {}
                : {};
            const nextStep = this.computeNextStep(locks, unlocked);

            if (nextStep) {
                // Advance to next step within the same modal
                // Update the title based on the next step
                let title = "Unlock required";
                if (nextStep === 3) {
                    title = "Content Warning";
                }
                this.unlockModalData = {
                    ...this.unlockModalData,
                    step: nextStep,
                    title,
                    reason: null,
                };
                return;
            }

            // No further steps: close modal
            const modalRef = this.$refs.bootstrapModal;
            if (modalRef && typeof modalRef.hideModal === "function") {
                modalRef.hideModal("unlockModal");
            }

            // After successful unlock of all required steps, resume the original action if any
            if (this.pendingAction) {
                const { url, user_id, type, link } = this.pendingAction;
                // Clear pending action first to avoid loops
                this.pendingAction = null;
                return this.performTrackedAction(url, user_id, type, link);
            }
        },
        normalizeMode(mode) {
            const m = String(mode || "")
                .toLowerCase()
                .trim();

            // ===== EMBED / INLINE =====
            if (
                m === "embed" ||
                m === "embed-typeform" || // Typeform inline embed
                m === "latest" || // YouTube
                m === "post_embed" || // X
                m === "preview-only" || // Music
                m === "custom" || // Google Maps
                m === "products_only" || // Shopify
                m === "profile_only" ||
                m === "streaming-and-preview" ||
                m === "profile_and_videos" ||
                m === "channel" ||
                m === "chat" ||
                m === "both"
            ) {
                return "embed";
            }

            // ===== LINK / DIRECT =====
            if (
                m === "link" ||
                m === "link-to-typeform" || // Typeform open in new tab
                m === "show_open_button" || // Typeform explicit open button
                m === "latest_link" || // YouTube
                m === "profile" || // X
                m === "go-directly" ||
                m === "direct" ||
                m === "link_only" ||
                m === "default"
            ) {
                return "link";
            }

            // Fallback
            return "link";
        },
        async handleClick(payloadOrUrl, user_id, type, link) {
            let payload = null;
            let urlLocal = payloadOrUrl;

            // Support new emit: { link, mode, action }
            if (typeof payloadOrUrl === "object") {
                payload = payloadOrUrl;
                link = payload.link;
                type = payload.mode;
                urlLocal = link.url;
            }

            const isPlayAction = payload?.action === "play";

            if (this.isTrackingDisabled) {
                return this.resumeOriginalAction(urlLocal, user_id, type, link);
            }

            // 1) Check lock first
            try {
                const res = await axios.post(
                    route("public.profile.checkLink"),
                    { link_id: link.id }
                );

                if (res?.data?.blocked) {
                    this.pendingAction = {
                        payload,
                        url: urlLocal,
                        user_id,
                        type,
                        link,
                    };
                    this.stageProps = res.data.step || 0;
                    this.openBootstrapUnlockModal(link, {
                        step: res.data.step,
                        reason: res.data.reason,
                        locks: res.data.locks,
                    });
                    return false;
                }
            } catch (err) {
                console.error(
                    "Link lock check failed, continuing locally:",
                    err
                );
            }

            // Default behavior
            return this.performTrackedAction(urlLocal, user_id, type, link);
        },
        async recordLinkClick(url, user_id, link) {
            if (this.isTrackingDisabled || !user_id || !link?.id) {
                return;
            }

            try {
                await axios.post(route("url"), {
                    url,
                    user_id,
                    page_id: link.page_id,
                    link_id: link.id,
                });
            } catch (error) {
                console.error("Unable to record link click:", error);
            }
        },
        async performTrackedAction(url, user_id, type, link) {
            await this.recordLinkClick(url, user_id, link);

            return this.resumeOriginalAction(url, user_id, type, link);
        },
        proceedWithAction(url, user_id, type, link) {
            // Ensure the unlock modal is closed before proceeding
            const modalRef = this.$refs.bootstrapModal;
            if (modalRef) {
                if (typeof modalRef.closeModal === "function") {
                    modalRef.closeModal("unlockModal");
                } else if (typeof modalRef.hideModal === "function") {
                    modalRef.hideModal("unlockModal");
                }
            }

            let $this = this;
            if (type == "contact-form") {
                try {
                    return this.$refs.contactFormRef.openContactFormById(link);
                } catch (error) {
                    console.error(
                        "Error showing contact details modal:",
                        error
                    );
                }
            } else if (type == "contact-details") {
                try {
                    return this.$refs.contactDetailsRef.openContactDetailsById(
                        link
                    );
                } catch (error) {
                    console.error(
                        "Error showing contact details modal:",
                        error
                    );
                }
            } else if (type == "Requests Stripe") {
                if (this.isTrackingDisabled) {
                    this.openExternalLink(link.payment_link_stripe);
                    return;
                }
                window.location.href = link.payment_link_stripe;
                return;
            } else if (type == "shopify" && link.layout_store == "classic") {
                $j("#shopify-list-" + link.id).modal("show");
                return false;
            } else if (
                link.options.youtube?.display_on_allmyonline == true ||
                link.options.youtube?.display_latest_video_on_allmyonline ==
                    true
            ) {
                this.$refs.setShowVideoTrue.openModalAndSetOptions(link);
                $j("#video-popup").modal("show");
                return false;
            } else if (
                link.platform === "typeform" &&
                link.options?.typeform?.embed
            ) {
                this.$refs.typeformRef.showModal(link);
                return false;
            } else if (
                link.platform === "calendly" &&
                link.options?.calendly?.display_mode === "embed"
            ) {
                this.$refs.calendlyRef.showModal(link);
                return false;
            } else if (this.shouldOpenMusicPopup(link)) {
                this.$refs.embedModalRef.openEmbedModalForMusic(link);
                return false;
            } else if (
                link.platform === "x" &&
                link.options?.x?.display_mode === "post_embed"
            ) {
                this.$refs.xRef.showModal(link);
                return false;
            } else if (this.shouldOpenTikTokProfileOnly(link)) {
                this.$refs.tiktokModalRef.showTikTokProfileModal(link);
                return;
            } else if (this.shouldOpenTikTokWithVideos(link)) {
                this.$refs.tiktokModalRef.openTikTokVideosModal(link);
                return;
            } else if (
                link.platform === "twitch" &&
                link.options?.twitch?.display_mode !== "link"
            ) {
                this.$refs.twitchEmbedRef.showModal(link);
                return false;
            } else if (
                link.platform === "facebook" &&
                link.options?.facebook?.display_mode === "post_embed"
            ) {
                // Call the card component and start inline iframe play
                try {
                    const refName = `card_${link.id}`; // example dynamic ref
                    const card = this.$refs[refName];
                    card.playUrl();
                    return false;
                } catch (e) {
                    console.error("Failed to start facebook embed:", e);
                }
            } else {
                this.openExternalLink(url);
            }
        },
        resumeOriginalAction(url, user_id, type, link) {
            // Determine the final mode similar to handleClick/openBootstrapUnlockModal
            const rawMode =
                type ||
                link?.options?.display_mode ||
                link?.options?.youtube?.display_mode ||
                link?.options?.x?.display_mode ||
                link?.options?.twitch?.display_mode ||
                link?.options?.facebook?.display_mode ||
                link?.options?.music?.display_mode ||
                link?.options?.calendly?.display_mode ||
                link?.options?.tiktok?.display_mode ||
                link?.options?.google_maps?.display_mode ||
                link?.options?.shopify?.display_mode;
            const finalMode = this.normalizeMode(rawMode);

            // Link-like behavior
            if (finalMode === "link") {
                // If the link is a contact modal, open the in-app modal instead of a new tab
                if (
                    (link &&
                        (link.platform === "contact-details" ||
                            link.platform === "contact-form")) ||
                    type === "contact-details" ||
                    type === "contact-form"
                ) {
                    this.proceedWithAction(url, user_id, type, link);
                    return;
                }
                this.openExternalLink(url);
                return;
            }

            // If embed is requested but platform is X or TikTok, force opening in a new tab
            if (
                finalMode === "embed" &&
                (link?.platform === "x" || link?.platform === "tiktok")
            ) {
                this.openExternalLink(url);
                return;
            }

            // Platform-specific inline actions immediately after unlocking
            if (finalMode === "embed" && link?.platform === "facebook") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playUrl === "function") {
                    inst.playUrl();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "youtube") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "calendly") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
                if (inst && typeof inst.openInline === "function") {
                    inst.openInline();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "google-maps") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
                if (inst && typeof inst.openInline === "function") {
                    inst.openInline();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "shopify") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
                if (inst && typeof inst.openInline === "function") {
                    inst.openInline();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "tiktok") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "soundcloud") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
            }
            if (finalMode === "embed" && link?.platform === "deezer") {
                const refName = `card_${link.id}`;
                const cardRef = this.$refs?.[refName];
                const inst = Array.isArray(cardRef) ? cardRef[0] : cardRef;
                if (inst && typeof inst.playInline === "function") {
                    inst.playInline();
                    return;
                }
            }

            // Fallback to the normal action handler
            this.proceedWithAction(url, user_id, type, link);
        },
        saveClickedSocialIcon(icon) {
            if (this.isTrackingDisabled || !this.user?.id) {
                return;
            }
            axios
                .post(
                    route("social"),
                    {
                        social_icon: icon.title.toLowerCase(),
                        user_id: this.user.id,
                    },
                    {
                        preserveScroll: true,
                        onError: () =>
                            console.log(
                                "There was an error saving the social icon click."
                            ),
                    }
                )
                .then(() => {
                    console.log(`Saved: ${icon}`);
                });
        },
        getStyleForIcon(appearance) {
            const isMono = this.iconColorType === "mono";
            switch (appearance.icon_style) {
                case "style1": // Circle
                    return {
                        backgroundColor: isMono
                            ? appearance.background_social_icon
                            : "#fff",
                        borderRadius: "100%",
                        padding: "5px",
                        margin: "3px",
                        boxShadow: isMono
                            ? "0px 4px 4px rgba(0, 0, 0, 0.25)"
                            : "0 1px 3px rgba(0, 0, 0, 0.1)",
                    };
                case "style2": // Square
                    return {
                        backgroundColor: isMono
                            ? appearance.background_social_icon
                            : "#fff",
                        borderRadius: "0.2rem",
                        padding: "5px",
                        margin: "3px",
                        boxShadow: isMono
                            ? "0px 4px 4px rgba(0, 0, 0, 0.25)"
                            : "0 1px 3px rgba(0, 0, 0, 0.1)",
                    };
                case "style3": // Transparent
                    return {
                        backgroundColor: "transparent",
                        borderRadius: "0",
                        padding: "0",
                        margin: "3px",
                        width: "32px",
                        height: "32px",
                    };
                default:
                    return {
                        backgroundColor: isMono
                            ? appearance.background_social_icon
                            : "#fff",
                        borderRadius: "50%",
                        padding: "5px",
                        margin: "3px",
                        boxShadow: isMono
                            ? "0px 4px 4px rgba(0, 0, 0, 0.25)"
                            : "0 1px 3px rgba(0, 0, 0, 0.1)",
                    };
            }
        },
    },
};
</script>

<style>
/* 1) Full-screen wrapper (replaces your old <body> styles) */
.page-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background: rgba(0, 0, 0, 0.05); /* subtle gray */
}
.page-wrapper.hero-page {
    justify-content: flex-start; /* remove vertical centering in hero */
    align-items: stretch;
    padding-top: 0 !important;
}

/* 2) Card styling */
.profile-card {
    position: relative;
    width: 100%;
    max-width: 100%;
    border-radius: 1rem;
    overflow: hidden; /* ensure hero content stays inside rounded card */
    padding: 0;
    margin-top: 0; /* ensure no top margin on mobile/tablet */
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.08);
    z-index: 1;
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}
/* header */
.profile-header {
    position: relative;
    text-align: center;
    z-index: 1;
}
.profile-header button {
    position: absolute;
    top: 1rem;
    width: 2.5rem;
    height: 2.5rem;
    border: none;
    cursor: pointer;
    display: flex; /* center SVG inside the circle */
    align-items: center;
    justify-content: center;
    padding: 0; /* remove default padding to truly center */
}
.profile-email-verification-banner {
    position: absolute;
    top: 0rem;
    left: 0rem;
    right: 0rem;
    z-index: 3;
    margin: 0;
    padding: 1rem 1.5rem 0.75rem;
    background: var(--danger-light);
    border-bottom: 1px solid var(--danger);
    color: var(--danger-dark);
    display: flex;
    gap: 0.625rem;
}
.profile-email-verification-banner__text {
    font-size: 0.875rem;
    line-height: 1.4;
    font-weight: 500;
}
.profile-email-verification-banner__text strong {
    display: block;
    margin-bottom: 0.125rem;
    font-weight: 700;
}
.profile-email-verification-banner__sent {
    margin-top: 0.375rem;
    font-size: 0.8125rem;
    font-weight: 600;
}
.profile-email-verification-banner__action {
    align-self: flex-start;
}
.profile-email-verification-banner__action--sent {
    pointer-events: none;
    cursor: default;
}
.btn-settings {
    left: 1rem;
    /* Ensure the gear/logo icon uses a neutral color on all devices */
    color: #333; /* currentColor for the SVG path */
}
.btn-settings:hover {
    color: #111;
}
.btn-share {
    right: 1rem;
}
.qr-container {
    position: fixed;
    bottom: 1rem;
    right: 1rem;
    text-align: center;
    z-index: -1;
    padding: 8px;
    border-radius: 10px;
    opacity: 0.8;
}
.qr-container canvas {
    display: block;
    margin: 0 auto;
    border-radius: 0.25rem;
}
.qr-container p {
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: #555;
}
@media (max-width: 640px) {
    .qr-container {
        display: none;
    }
    .profile-card {
        width: 100% !important;
        max-width: 100% !important;
        height: 100% !important;
        min-height: 100vh; /* ensure full viewport height on mobile */
        padding: 0;
        border-radius: 0 !important;
        border: none !important; /* remove any border on mobile */
    }
}
/* Ensure no rounded corners or borders on tablets (incl. iPad) */
@media (max-width: 1023px) {
    .profile-card {
        border-radius: 0 !important;
        border: none !important;
    }
}

.footer-section {
    text-align: center;
    margin-top: auto;
}
.links {
    flex: 1 1 auto;
    padding: 5px;
}
.full-height {
    min-height: 100dvh;
}
@media (min-width: 640px) {
    .profile-email-verification-banner {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
    .profile-email-verification-banner__action {
        flex-shrink: 0;
    }
}
.profile-card {
    position: relative;
    width: 100%;
    max-width: 100%;
    border-radius: 1rem;
    overflow: hidden; /* ensure hero content stays inside rounded card */
    padding: 0;
    margin-top: 0; /* ensure no top margin on mobile/tablet */
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.08);
    z-index: 1;
    display: flex;
    flex-direction: column;
}

/* Hero mode removes card chrome so hero can merge with background */
.profile-card.hero-mode {
    box-shadow: none;
}
.profile-card.hero-mode .hero-profile {
    margin-top: 0 !important;
}
.profile-card.hero-mode .profile-header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2;
}

/* dim page bg */
.overlay {
    position: fixed;
    inset: 0;
    background: black;
    opacity: 0.05; /* almost invisible dark tint */
    pointer-events: none;
    z-index: 0;
}
.app-button--secondary {
    display: inline-flex; /* keeps icon + text inline */
    align-items: center;
    justify-content: center;
    gap: 0.5ch;
    font-size: 0.95rem;
    font-weight: 400;
    color: #444;
    background-color: rgba(255, 255, 255, 0.85);
    border: 1px solid #e0e0e0;
    padding: 0.6rem 1rem;
    border-radius: 0.75rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: background-color 150ms ease;
    margin: 0 auto; /* centers the button horizontally */
}
.app-button--secondary:hover {
    background-color: rgba(255, 255, 255, 0.95);
}
</style>

<!-- old style -->
<style>
.logo-section img {
    width: 100px;
    height: auto;
}
.btn-wrapper {
    display: inline-block;
}

.text {
    transform: translateY(50%);
}

/* Hide scrollbar in iframe - Chrome, Safari, Opera */
html.in-iframe::-webkit-scrollbar,
html.in-iframe body::-webkit-scrollbar {
    display: none !important;
}

/* Hide scrollbar in iframe - Firefox only */
@-moz-document url-prefix() {
    html.in-iframe,
    html.in-iframe body {
        scrollbar-width: none !important;
    }
}

</style>

<!-- old style -->
<style>
[class^="number-slide"],
[class*=" number-slide"] {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 50px;
    color: #fff;
    font-weight: 500;
    height: 200px;
    max-height: 100vh;
    border-radius: 24px;
    padding: 0;
}
[class^="number-slide-media"],
[class*="number-slide-media"] {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 50px;
    color: #fff;
    font-weight: 500;
    height: 100px;
    max-height: 100vh;
    border-radius: 24px;
    padding: 0;
}
/* Position Shopify carousel navigation arrows at the bottom of the card */
.link-button .carousel__prev,
.link-button .carousel__next {
    top: auto !important;
    bottom: 12px; /* Place at bottom of card */
    transform: none; /* Override default vertical centering */
    z-index: 3; /* Ensure arrows appear above content */
}
/* Keep pagination at the bottom center, not overlapping arrows */
.link-button .carousel__pagination {
    bottom: 12px;
}
.text-i {
    margin: auto;
}
button.animate {
    animation: pulse-strong 3s ease-in-out;
}
div.animate {
    animation: pulse-strong 3s ease-in-out;
}

@keyframes pulse-strong {
    0% {
        transform: scale(1);
        background-color: #007bff;
        box-shadow: none;
    }
    25% {
        transform: scale(1.15); /* Scale larger for emphasis */
        background-color: #00bfff;
        box-shadow: 0 0 10px 5px rgba(0, 191, 255, 0.7); /* Add glowing shadow */
    }
    50% {
        transform: scale(1.1);
        background-color: #ff9800;
        box-shadow: 0 0 15px 8px rgba(255, 152, 0, 0.8); /* Change glow color */
    }
    75% {
        transform: scale(1.2); /* Max scale for stronger effect */
        background-color: #00e676;
        box-shadow: 0 0 20px 10px rgba(0, 230, 118, 0.9); /* Stronger glow */
    }
    100% {
        transform: scale(1);
        background-color: #007bff; /* Revert to original state */
        box-shadow: none; /* Remove glow */
    }
}

/* Shared hover effect */

.swiper-container {
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Swiper Container */
.swiper-container {
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* Swiper Wrapper */
.swiper-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Social Card Link */
.social-card-link {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    text-align: center;
    transition: transform 0.2s;
}

.social-card-link:hover {
    transform: scale(1.1);
}
.links-section btn.rounded-1 {
}

.tiktok-video-item img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}
/* Social icon SVG - common styling for both top and bottom positions */
.social-icon-svg {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0.25rem;
    line-height: 0;
}

.social-icon-svg svg {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
}

.social-icon-svg--styled {
    color: var(--userprofile-social-icons);
}

/* Desktop-only profile card overrides */
@media (min-width: 1024px) {
    .profile-card {
        margin: auto;
        border-radius: 4rem;
        box-shadow: none;
        border: 5px solid black;
        display: flex;
        justify-content: center;
        margin-top: 30px;
        max-width: 392px;
    }
}
</style>
